import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useClerk, useSession, useSignIn } from '@clerk/react';
import {
  AUTO_SIGN_IN_EMAIL_PARAM,
  AUTO_SIGN_IN_REDIRECT_PARAM,
  AUTO_SIGN_IN_TICKET_PARAM,
  autoSignInFallbackPath,
  isAutoSignInTicketExpired,
  isInternalRedirectPath,
  SessionType,
} from '@shared';
import { selectSession } from '@client/model/selectors';
import { useClerkClientAuth } from './useClerkClientAuth';

export type TicketSignInState =
  | 'deciding'
  | 'confirm'
  | 'redeeming'
  | 'chooseSession'
  | 'switchingAccount'
  | 'invalidLink';

interface UseTicketSignInResult {
  state: TicketSignInState;
  confirmSignIn: () => void;
  continueInCurrentSession: () => void;
  switchAccount: () => void;
}

const DEFAULT_REDIRECT = '/client/home';
const SESSION_WAIT_TIMEOUT_MS = 8000;

/**
 * Reads the ticket out of the URL fragment and removes it from the address bar
 * in the same tick.
 *
 * Runs at module scope on first import rather than in an effect so the
 * credential is gone before React renders and before anything reads
 * `window.location`. Telemetry that samples the URL later — our own `Events`
 * pageUrl, session replay, breadcrumbs — therefore never sees it. A cold page
 * load can still be captured by SDKs that snapshot the URL during their own
 * initialisation, which happens before any route code runs.
 */
// Holds the ticket for the lifetime of the page load. Necessary because
// takeTicketFromFragment is destructive: it removes the ticket from the URL, so a
// second call finds nothing. React.StrictMode double-invokes state initialisers
// in development, which made the second call return null and report a perfectly
// valid link as invalid. A fresh page load re-initialises this module, and a
// remount carrying a *new* ticket overwrites it below.
let capturedTicket: string | null = null;

function captureTicketOnce(): string | null {
  const fromUrl = takeTicketFromFragment();
  if (fromUrl) capturedTicket = fromUrl;
  return capturedTicket;
}

function takeTicketFromFragment(): string | null {
  if (typeof window === 'undefined') return null;

  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  if (!hash) return null;

  const fragmentParams = new URLSearchParams(hash);
  const ticket = fragmentParams.get(AUTO_SIGN_IN_TICKET_PARAM);
  if (!ticket) return null;

  fragmentParams.delete(AUTO_SIGN_IN_TICKET_PARAM);
  const remaining = fragmentParams.toString();
  const scrubbed = `${window.location.pathname}${window.location.search}${
    remaining ? `#${remaining}` : ''
  }`;
  window.history.replaceState(window.history.state, '', scrubbed);

  return ticket;
}

/**
 * Redeems a Clerk sign-in token from the URL and forwards to `redirect_to`.
 *
 * Clerk owns expiry and single-use, so there is no window to check here — a
 * rejected ticket is simply routed to the manual sign-in fallback, which carries
 * the same destination so the recipient still lands where the link intended.
 */
export function useTicketSignIn(): UseTicketSignInResult {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useSignIn();
  const { signOut } = useClerk();
  const { session: clerkSession } = useSession();
  const { isClerkSignedIn, isClerkLoading } = useClerkClientAuth();
  const session = useSelector(selectSession);

  // Held only in memory; the URL no longer contains it after the first read.
  const [ticket] = useState(() => captureTicketOnce());
  const emailHint = searchParams.get(AUTO_SIGN_IN_EMAIL_PARAM);
  const requestedRedirect = searchParams.get(AUTO_SIGN_IN_REDIRECT_PARAM);
  const redirectTo = isInternalRedirectPath(requestedRedirect)
    ? requestedRedirect!
    : DEFAULT_REDIRECT;

  // Starts neutral: nothing is decided until Clerk and the app session have both
  // resolved, so no branch is rendered before we know which one applies.
  const [state, setState] = useState<TicketSignInState>(ticket ? 'deciding' : 'invalidLink');

  // A sign-in token is single-use. StrictMode's double effect invocation, a
  // re-render, or the back button would otherwise burn it on the second attempt
  // and send an already-signed-in recipient to the failure page.
  const hasRedeemed = useRef(false);

  // Set while switchAccount drives its own sign-out-then-redeem sequence, so the
  // deciding effect does not stomp on it when the auth state flips mid-flow.
  const isSwitching = useRef(false);

  const goToFallback = useCallback(() => {
    navigate(autoSignInFallbackPath({ redirectTo, email: emailHint }), { replace: true });
  }, [navigate, redirectTo, emailHint]);

  const redeem = useCallback(async () => {
    if (!ticket || hasRedeemed.current) return;
    hasRedeemed.current = true;
    setState('redeeming');

    // Every exit below must either navigate or fall back. A thrown error or a
    // silent no-op would leave the recipient on a spinner with the ticket
    // already spent and no way forward.
    try {
      const { error: ticketError } = await signIn.ticket({ ticket });
      if (ticketError) {
        console.error('[useTicketSignIn] ticket error:', JSON.stringify(ticketError, null, 2));
        goToFallback();
        return;
      }

      if (signIn.status !== 'complete') {
        // A second factor or a Clerk task is outstanding; finalize would not land
        // them anywhere useful, so hand off to the normal sign-in screens.
        console.warn('[useTicketSignIn] sign-in not complete after ticket', {
          status: signIn.status,
        });
        goToFallback();
        return;
      }

      let didNavigate = false;
      const { error: finalizeError } = await signIn.finalize({
        navigate: ({ session: finalizedSession, decorateUrl }) => {
          // Clerk owns the flow while a task is pending, so leave the navigation
          // to it rather than fighting for control.
          if (finalizedSession?.currentTask) return;
          didNavigate = true;
          const url = decorateUrl(redirectTo);
          // `replace` on both branches so the spent ticket does not sit in
          // history for the back button to resubmit.
          if (url.startsWith('http')) window.location.replace(url);
          else navigate(url, { replace: true });
        },
      });

      if (finalizeError) {
        console.error('[useTicketSignIn] finalize error:', JSON.stringify(finalizeError, null, 2));
        goToFallback();
        return;
      }

      // Signed in, but Clerk had a task pending so nothing routed us onward.
      // Send them to `/` and let SessionLoader place them.
      if (!didNavigate) {
        console.warn('[useTicketSignIn] signed in with a pending Clerk task');
        navigate('/', { replace: true });
      }
    } catch (error) {
      console.error('[useTicketSignIn] unexpected redeem failure:', error);
      goToFallback();
    }
  }, [ticket, signIn, goToFallback, navigate, redirectTo]);

  // The recipient's click is the explicit gesture that authorises redemption.
  const confirmSignIn = useCallback(() => {
    void redeem();
  }, [redeem]);

  // Staying in the current session means NOT following the link's destination —
  // that belongs to whoever the link was sent to. Land on `/` and let
  // SessionLoader route to the dashboard for whichever role is signed in.
  const continueInCurrentSession = useCallback(() => {
    navigate('/', { replace: true });
  }, [navigate]);

  const switchAccount = useCallback(async () => {
    // Check before signing out. Otherwise an expired token ends a perfectly good
    // session and only then discovers there is nothing to sign in with, leaving
    // the recipient signed out at the login page.
    if (ticket && isAutoSignInTicketExpired(ticket)) {
      goToFallback();
      return;
    }

    isSwitching.current = true;
    setState('switchingAccount');

    // `redirectUrl` is this same route so Clerk's post-sign-out navigation does
    // not send us to `afterSignOutUrl` and unmount the page. That matters more
    // than it used to: the ticket now lives only in memory, so an unmount would
    // lose it with no way to recover it from the URL.
    const returnUrl = `${location.pathname}${location.search}`;
    try {
      // Scoped by sessionId so any other session on this browser is left alone.
      await signOut({ sessionId: clerkSession?.id, redirectUrl: returnUrl });
      // Clicking "Switch accounts" already was the explicit gesture, so redeem
      // rather than asking for a second confirmation.
      await redeem();
    } catch (error) {
      // Nothing re-runs the deciding effect on a rejected sign-out, so without
      // this the chooser's buttons are gone and only a spinner remains. Restore
      // the chooser rather than falling back to manual sign-in: the ticket is
      // still unspent, so retrying is worth more than burning it.
      console.error('[useTicketSignIn] sign-out failed:', error);
      isSwitching.current = false;
      setState('chooseSession');
    }
  }, [signOut, clerkSession?.id, location.pathname, location.search, redeem, ticket, goToFallback]);

  // The signed-in session may already cover the client this link targets (the
  // recipient simply still had a session open). `redirect_to` carries that
  // client id, so compare against the session's own profiles rather than
  // prompting unnecessarily.
  const targetClientId = extractClientId(redirectTo);
  const sessionCoversTarget = Boolean(
    targetClientId && session.clientProfiles?.some(profile => profile.clientId === targetClientId),
  );

  // `session.isLoading` is false before the session fetch even starts, so it
  // cannot stand in for "resolved" — sessionType is what actually transitions
  // away from `unknown` once the session lands (including to `unauthenticated`
  // when the fetch is cleared).
  const isSessionResolved = session.sessionType !== SessionType.unknown && !session.isLoading;

  // Backstop: never leave the recipient on a spinner with no way forward if the
  // session never resolves for a reason we did not anticipate.
  const [waitedForSession, setWaitedForSession] = useState(false);
  useEffect(() => {
    if (!ticket || isSessionResolved || !isClerkSignedIn) return;
    const id = setTimeout(() => setWaitedForSession(true), SESSION_WAIT_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [ticket, isSessionResolved, isClerkSignedIn]);

  useEffect(() => {
    if (!ticket) {
      setState('invalidLink');
      return;
    }
    if (isClerkLoading || hasRedeemed.current || isSwitching.current) return;

    if (!isClerkSignedIn) {
      // A token past its own `exp` cannot be redeemed, so asking for a click
      // first would only add a step before the inevitable bounce. Send them
      // straight to manual sign-in, which still carries the destination.
      if (isAutoSignInTicketExpired(ticket)) {
        goToFallback();
        return;
      }
      // Otherwise: deliberately do NOT redeem here. Redemption requires an
      // explicit click, because a plain GET of this URL must never establish a
      // session — link scanners and inbox preview fetchers (several of which run
      // JavaScript) would otherwise consume the single-use ticket before the
      // recipient opens the mail, and a ticket pasted into a link sent to
      // someone else would sign them in silently.
      setState('confirm');
      return;
    }

    // Signed in already. Hold on the neutral state until the session resolves,
    // otherwise the chooser flashes before we know whether this is the same
    // person.
    if (!isSessionResolved && !waitedForSession) {
      setState('deciding');
      return;
    }

    if (sessionCoversTarget) {
      navigate(redirectTo, { replace: true });
      return;
    }

    setState('chooseSession');
  }, [
    ticket,
    isClerkLoading,
    isClerkSignedIn,
    isSessionResolved,
    waitedForSession,
    sessionCoversTarget,
    redeem,
    navigate,
    redirectTo,
  ]);

  return { state, confirmSignIn, continueInCurrentSession, switchAccount };
}

function extractClientId(path: string): string | null {
  const match = /^\/clients\/([^/?#]+)/.exec(path);
  return match ? match[1] : null;
}
